import { useEffect, useId, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, auth } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { MultiSelect } from '../components/MultiSelect';
import { useToast } from '../components/Toast';
import RichTextEditor from '../components/RichTextEditor';
import RichTextView from '../components/RichTextView';
import { isRichTextEmpty } from '../utils/richtext';
import type {
  AnswerContentKind, AnswerSheetConfig, AuthorExam, AuthorQuestion, BatchDto, CreateEitherOrRequest,
  CourseOutcome, CreateQuestionRequest, DepartmentDto, EitherOrAlternativeRequest, ExamScheduleDto, MainSheetType, ProgramDto,
  QuestionType, RubricCategory, UpdateExamRequest, UpdateQuestionRequest,
  UpsertRubricCriterionRequest
} from '../types/models';

// The default booklet a new exam starts with (ruled, one starting page).
const DEFAULT_ANSWER_SHEETS: AnswerSheetConfig = { mainSheet: 'ruled', mainPages: 1, extras: [] };

// Keys for grouping scheduled rows in the "Start from a scheduled exam" picker.
// One scheduled subject can span several departments/batches — those share these keys.
const examKeyOf = (s: ExamScheduleDto) => s.examPeriodId ?? s.examName;
const subjectKeyOf = (s: ExamScheduleDto) => (s.subjectCode || s.subjectName).toLowerCase();

// Bloom's Taxonomy levels shown in the per-question CO/BL picker.
const BLOOM_LEVELS: { v: number; label: string }[] = [
  { v: 1, label: 'L1 – Remembering' },
  { v: 2, label: 'L2 – Understanding' },
  { v: 3, label: 'L3 – Applying' },
  { v: 4, label: 'L4 – Analysing' },
  { v: 5, label: 'L5 – Evaluating' },
  { v: 6, label: 'L6 – Creating' },
];

/** Bloom's-level multi-select (a question can map to several levels, e.g. L1,L5). */
function BloomChecks({ value, onChange }: { value: number[]; onChange: (v: number[]) => void }) {
  const toggle = (n: number) =>
    onChange(value.includes(n) ? value.filter(x => x !== n) : [...value, n].sort((a, b) => a - b));
  return (
    <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>
      {BLOOM_LEVELS.map(b => (
        <label key={b.v} className="row" style={{ gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={value.includes(b.v)} onChange={() => toggle(b.v)} style={{ width: 'auto' }} />
          <span style={{ fontSize: 13 }}>{b.label}</span>
        </label>
      ))}
    </div>
  );
}

/** Course-Outcome field: a dropdown of the exam's defined COs, or free text if none defined yet. */
function CoField({ value, onChange, courseOutcomes }:
  { value: string; onChange: (v: string) => void; courseOutcomes: CourseOutcome[] }) {
  if (courseOutcomes.length === 0)
    return <input value={value} maxLength={50} placeholder="e.g. CO1" onChange={e => onChange(e.target.value)} />;
  return (
    <select value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— None —</option>
      {courseOutcomes.map(c => (
        <option key={c.code} value={c.code}>{c.code}{c.description ? ` — ${c.description}` : ''}</option>
      ))}
    </select>
  );
}

/** Editor for an exam's Course Outcomes list (CO1…), shown as the CO legend on the paper. */
function CourseOutcomesEditor({ value, onChange }:
  { value: CourseOutcome[]; onChange: (v: CourseOutcome[]) => void }) {
  const add = () => onChange([...value, { code: `CO${value.length + 1}`, description: '' }]);
  const patch = (i: number, p: Partial<CourseOutcome>) =>
    onChange(value.map((c, j) => j === i ? { ...c, ...p } : c));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  return (
    <div className="card" style={{ background: 'var(--surface-2)' }}>
      <h3 style={{ marginTop: 0 }}>Course Outcomes (CO)</h3>
      <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
        Define the COs for this exam. Each question can then be mapped to one of these, and they
        print as the CO legend on the paper.
      </div>
      {value.map((c, i) => (
        <div key={i} className="row" style={{ gap: 8, marginBottom: 6 }}>
          <input style={{ flex: '0 0 90px' }} value={c.code} maxLength={16} placeholder="CO1"
                 onChange={e => patch(i, { code: e.target.value })} />
          <input style={{ flex: 1 }} value={c.description} maxLength={400} placeholder="Description"
                 onChange={e => patch(i, { description: e.target.value })} />
          <button type="button" onClick={() => remove(i)}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={add} style={{ marginTop: 4 }}>+ Add CO</button>
    </div>
  );
}

/** Staff picks only the main sheet style (ruled/unruled) and how many pages to start with.
 *  Students choose any extra sheets (graph, engineering, etc.) themselves during the exam. */
function AnswerSheetsFields({ value, onChange }: {
  value: AnswerSheetConfig;
  onChange: (next: AnswerSheetConfig) => void;
}) {
  return (
    <div className="card" style={{ background: 'var(--surface-2)' }}>
      <h3 style={{ marginTop: 0 }}>Answer sheets</h3>
      <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
        Choose the main answer sheet for this exam. Students can’t change this style — but they can
        pull in extra sheets (graph, engineering, semi-log, plain) themselves while writing.
      </div>
      <label style={{ display: 'block', maxWidth: 320 }}>
        <div className="muted">Main answer sheet</div>
        <select value={value.mainSheet}
                onChange={e => onChange({ ...value, mainSheet: e.target.value as MainSheetType })}>
          <option value="ruled">Ruled (lined)</option>
          <option value="plain">Unruled (blank)</option>
        </select>
      </label>
    </div>
  );
}

export function ExamAuthorPage() {
  const toast = useToast();
  const { examId, orgId } = useParams();
  const nav = useNavigate();
  const isCreate = !examId || examId === 'new';
  const user = auth.getUser();
  // Admins author exams inside the org sidebar (orgId present); faculty use the standalone page.
  const embedded = !!orgId;
  const examsHome = embedded ? `/org/${orgId}/exams` : '/exams';
  const authoredPath = (id: string) => embedded ? `/org/${orgId}/exams/${id}` : `/author/${id}`;
  const paperPath = (id: string) => embedded ? `/org/${orgId}/exams/${id}/paper` : `/author/${id}/paper`;

  const [exam, setExam] = useState<AuthorExam | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);

  // Create-mode state
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [batchOptions, setBatchOptions] = useState<BatchDto[]>([]);
  // '' = All departments. Program & batch selections are multi.
  const [departmentId, setDepartmentId] = useState('');
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<string[]>([]);
  const [createForm, setCreateForm] = useState({
    title: '',
    subjectName: '',
    subjectCode: '',
    regulation: '',
    instructions: '',
    durationMinutes: 90,
    scheduledStart: defaultDateLocal(0),
    scheduledEnd: defaultDateLocal(7 * 24)
  });
  const [answerSheets, setAnswerSheets] = useState<AnswerSheetConfig>(DEFAULT_ANSWER_SHEETS);
  const [courseOutcomes, setCourseOutcomes] = useState<CourseOutcome[]>([]);
  // Already-scheduled subjects (from Exam Schedule) that can pre-fill this form so you don't re-enter.
  const [schedules, setSchedules] = useState<ExamScheduleDto[]>([]);
  const [pendingBatchIds, setPendingBatchIds] = useState<string[]>([]);
  // Two-step scheduled-exam picker: choose an exam name, then a subject within it.
  const [pickedExamKey, setPickedExamKey] = useState('');
  const [pickedSubjectKey, setPickedSubjectKey] = useState('');
  const [searchParams] = useSearchParams();
  const fromSchedule = searchParams.get('fromSchedule');

  useEffect(() => {
    if (isCreate) {
      api.listDepartments(orgId).then(setDepartments).catch(e => setError(e.message));
      api.listPrograms(orgId).then(setPrograms).catch(() => setPrograms([]));
      if (orgId) api.listExamSchedules(orgId).then(setSchedules).catch(() => setSchedules([]));
      return;
    }
    api.getAuthorExam(examId!).then(setExam).catch(e => setError(e.message));
  }, [examId, isCreate, orgId]);

  /** Pre-fill the create form from a group of scheduled rows (same exam+subject, possibly
   *  across several departments/batches) so a single paper covers all of them. */
  function applyScheduleRows(rows: ExamScheduleDto[]) {
    if (rows.length === 0) return;
    const first = rows[0];
    const depIds = [...new Set(rows.map(r => r.departmentId))];
    const progIds = [...new Set(rows.map(r => r.programId))];
    const batchIds = [...new Set(rows.map(r => r.batchId))];
    // Span the whole group's window: earliest open → latest close.
    const start = rows.reduce((a, r) => (r.scheduledStart < a ? r.scheduledStart : a), first.scheduledStart);
    const end = rows.reduce((a, r) => (r.scheduledEnd > a ? r.scheduledEnd : a), first.scheduledEnd);
    setCreateForm(f => ({
      ...f,
      title: `${first.examName} — ${first.subjectName}`,
      subjectName: first.subjectName,
      subjectCode: first.subjectCode ?? '',
      durationMinutes: first.durationMinutes,
      scheduledStart: toLocalInput(start),
      scheduledEnd: toLocalInput(end),
    }));
    // Single department → preselect it; spanning several → "All" so every program stays visible.
    setDepartmentId(depIds.length === 1 ? depIds[0] : '');
    setSelectedPrograms(progIds);
    setPendingBatchIds(batchIds);
  }

  /** Pre-fill from one scheduled row, aggregating every sibling row for the same exam+subject. */
  function applyFromSchedule(s: ExamScheduleDto) {
    setPickedExamKey(examKeyOf(s));
    setPickedSubjectKey(subjectKeyOf(s));
    applyScheduleRows(schedules.filter(x => examKeyOf(x) === examKeyOf(s) && subjectKeyOf(x) === subjectKeyOf(s)));
  }

  // When arriving via a "Create questions" link, pre-fill from that scheduled subject once loaded.
  useEffect(() => {
    if (!isCreate || !fromSchedule || schedules.length === 0) return;
    const s = schedules.find(x => x.id === fromSchedule);
    if (s) applyFromSchedule(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, fromSchedule, schedules]);

  // Apply a pending batch pre-selection once its program's batches have loaded (survives the prune below).
  useEffect(() => {
    if (pendingBatchIds.length === 0) return;
    const present = pendingBatchIds.filter(id => batchOptions.some(b => b.id === id));
    if (present.length) { setSelectedBatches(present); setPendingBatchIds([]); }
  }, [batchOptions, pendingBatchIds]);

  // Programs in scope: all (when department = All) or just the chosen department's.
  const programsForForm = departmentId ? programs.filter(p => p.departmentId === departmentId) : programs;

  // Batches = union of batches under the selected programs.
  useEffect(() => {
    if (selectedPrograms.length === 0) { setBatchOptions([]); return; }
    let cancelled = false;
    Promise.all(selectedPrograms.map(pid => api.listBatches(pid).catch(() => [])))
      .then(lists => {
        if (cancelled) return;
        const byId = new Map<string, BatchDto>();
        lists.flat().forEach(b => byId.set(b.id, b));
        setBatchOptions([...byId.values()]);
      });
    return () => { cancelled = true; };
  }, [selectedPrograms]);

  // Drop any selected batch that's no longer offered by the chosen programs.
  useEffect(() => {
    setSelectedBatches(prev => prev.filter(id => batchOptions.some(b => b.id === id)));
  }, [batchOptions]);


  async function reload() {
    if (!exam) return;
    const fresh = await api.getAuthorExam(exam.id);
    setExam(fresh);
  }

  async function handleCreateExam() {
    if (!createForm.title.trim()) {
      alert('Title is required.'); return;
    }
    try {
      const newId = await api.createExam({
        title: createForm.title,
        instructions: createForm.instructions || null,
        scheduledStart: new Date(createForm.scheduledStart).toISOString(),
        scheduledEnd: new Date(createForm.scheduledEnd).toISOString(),
        durationMinutes: Number(createForm.durationMinutes),
        organizationId: orgId ?? null,
        batchIds: selectedBatches,
        answerSheets,
        subjectName: createForm.subjectName.trim() || null,
        subjectCode: createForm.subjectCode.trim() || null,
        regulation: createForm.regulation.trim() || null,
        courseOutcomes: courseOutcomes.filter(c => c.code.trim()),
      });
      toast.success('Exam created.');
      nav(authoredPath(newId));
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to create exam.';
      alert(msg);
      toast.error(msg);
    }
  }

  async function publish() {
    if (!exam) return;
    if (!confirm('Publish this exam? Once published you cannot edit questions.')) return;
    try {
      await api.publishExam(exam.id);
      await reload();
      toast.success('Exam published.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to publish.';
      alert(msg);
      toast.error(msg);
    }
  }

  if (error) return <div className="container"><div className="card error">{error}</div></div>;

  // ---------- CREATE MODE ----------
  if (isCreate) {
    // Distinct exams for the first picker (one per exam period / exam name).
    const examGroups = (() => {
      const map = new Map<string, { key: string; examName: string; batchYearName: string | null }>();
      for (const s of schedules) {
        const k = examKeyOf(s);
        if (!map.has(k)) map.set(k, { key: k, examName: s.examName, batchYearName: s.batchYearName });
      }
      const arr = [...map.values()];
      const nameCounts = new Map<string, number>();
      arr.forEach(g => nameCounts.set(g.examName, (nameCounts.get(g.examName) ?? 0) + 1));
      // Only append the batch year when two periods share the same exam name.
      return arr.map(g => ({
        key: g.key,
        label: nameCounts.get(g.examName)! > 1 && g.batchYearName ? `${g.examName} · ${g.batchYearName}` : g.examName,
      }));
    })();

    // Distinct subjects within the picked exam, each noting how many departments it covers.
    const subjectsForExam = (() => {
      if (!pickedExamKey) return [] as { key: string; label: string; deptCount: number }[];
      const map = new Map<string, { subjectName: string; subjectCode: string | null; depts: Set<string> }>();
      for (const s of schedules.filter(x => examKeyOf(x) === pickedExamKey)) {
        const k = subjectKeyOf(s);
        const ex = map.get(k);
        if (ex) ex.depts.add(s.departmentId);
        else map.set(k, { subjectName: s.subjectName, subjectCode: s.subjectCode, depts: new Set([s.departmentId]) });
      }
      return [...map.entries()].map(([key, v]) => ({
        key,
        label: `${v.subjectName}${v.subjectCode ? ` (${v.subjectCode})` : ''}` +
          (v.depts.size > 1 ? ` · ${v.depts.size} departments` : ''),
        deptCount: v.depts.size,
      }));
    })();

    const createCard = (
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Create exam</h2>
            <div className="col">
              {schedules.length > 0 && (
                <div>
                  <div className="muted">Start from a scheduled exam (optional — pre-fills the details below)</div>
                  <div className="row" style={{ gap: 12, marginTop: 4 }}>
                    <select style={{ flex: 1 }} value={pickedExamKey} onChange={e => {
                      setPickedExamKey(e.target.value);
                      setPickedSubjectKey('');
                    }}>
                      <option value="">— Pick an exam —</option>
                      {examGroups.map(g => (
                        <option key={g.key} value={g.key}>{g.label}</option>
                      ))}
                    </select>
                    <select style={{ flex: 1 }} value={pickedSubjectKey} disabled={!pickedExamKey} onChange={e => {
                      const subj = e.target.value;
                      setPickedSubjectKey(subj);
                      if (subj) applyScheduleRows(
                        schedules.filter(x => examKeyOf(x) === pickedExamKey && subjectKeyOf(x) === subj));
                    }}>
                      <option value="">{pickedExamKey ? '— Pick a subject —' : 'Pick an exam first'}</option>
                      {subjectsForExam.map(su => (
                        <option key={su.key} value={su.key}>{su.label}</option>
                      ))}
                    </select>
                  </div>
                  {pickedSubjectKey && (subjectsForExam.find(s => s.key === pickedSubjectKey)?.deptCount ?? 0) > 1 && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                      This subject is scheduled for multiple departments — one paper will be created for all of them.
                    </div>
                  )}
                </div>
              )}
              <label>
                <div className="muted">Title</div>
                <input value={createForm.title}
                       onChange={e => setCreateForm({ ...createForm, title: e.target.value })} />
              </label>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Subject name</div>
                  <input value={createForm.subjectName} placeholder="e.g. Data Structures"
                         onChange={e => setCreateForm({ ...createForm, subjectName: e.target.value })} />
                </label>
                <label style={{ flex: '0 0 200px' }}>
                  <div className="muted">Subject code</div>
                  <input value={createForm.subjectCode} placeholder="e.g. CS8391"
                         onChange={e => setCreateForm({ ...createForm, subjectCode: e.target.value })} />
                </label>
                <label style={{ flex: '0 0 200px' }}>
                  <div className="muted">Regulation</div>
                  <input value={createForm.regulation} placeholder="e.g. Regulation 2023"
                         onChange={e => setCreateForm({ ...createForm, regulation: e.target.value })} />
                </label>
              </div>
              <label>
                <div className="muted">Instructions (optional)</div>
                <textarea rows={3} value={createForm.instructions}
                          onChange={e => setCreateForm({ ...createForm, instructions: e.target.value })} />
              </label>
              <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Department</div>
                  <select value={departmentId}
                          onChange={e => { setDepartmentId(e.target.value); setSelectedPrograms([]); setSelectedBatches([]); }}>
                    <option value="">All</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
                  </select>
                </label>
                <div style={{ flex: 1 }}>
                  <div className="muted">Program (select one or more)</div>
                  <div style={{ marginTop: 4 }}>
                    <MultiSelect
                      options={programsForForm.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
                      selected={selectedPrograms}
                      onChange={setSelectedPrograms}
                      placeholder="Select programs…"
                      emptyText="No programs"
                    />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="muted">Batch (students who'll see this exam)</div>
                  <div style={{ marginTop: 4 }}>
                    <MultiSelect
                      options={batchOptions.map(c => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                      selected={selectedBatches}
                      onChange={setSelectedBatches}
                      disabled={selectedPrograms.length === 0}
                      placeholder={selectedPrograms.length === 0 ? 'Pick a program first' : 'Select batches…'}
                      emptyText="No batches"
                    />
                  </div>
                </div>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Window opens</div>
                  <input type="datetime-local" value={createForm.scheduledStart}
                         onChange={e => setCreateForm({ ...createForm, scheduledStart: e.target.value })} />
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Window closes</div>
                  <input type="datetime-local" value={createForm.scheduledEnd}
                         onChange={e => setCreateForm({ ...createForm, scheduledEnd: e.target.value })} />
                </label>
                <label style={{ flex: '0 0 120px' }}>
                  <div className="muted">Duration (min)</div>
                  <input type="number" min={1} value={createForm.durationMinutes}
                         onChange={e => setCreateForm({ ...createForm, durationMinutes: Number(e.target.value) })} />
                </label>
              </div>
              <AnswerSheetsFields value={answerSheets} onChange={setAnswerSheets} />
              <CourseOutcomesEditor value={courseOutcomes} onChange={setCourseOutcomes} />
              <div className="row" style={{ justifyContent: 'flex-end' }}>
                <button className="primary" onClick={handleCreateExam}>Create draft</button>
              </div>
            </div>
          </div>
    );

    if (embedded) return <OrgLayout><h1 style={{ marginTop: 0 }}>Create exam</h1>{createCard}</OrgLayout>;
    return (
      <>
        <header className="topbar">
          <div className="brand">Exam App · Author</div>
          <button onClick={() => nav(examsHome)} className="topbar-ghost">Back</button>
        </header>
        <div className="container">{createCard}</div>
      </>
    );
  }

  if (!exam) return <div className="container"><div className="card muted">Loading…</div></div>;

  // ---------- EDIT MODE ----------
  const isDraft = exam.status === 'Draft';
  const isAdmin = user?.role === 'Admin';
  // Drafts are editable by any authorized author; admins may also edit a published exam
  // (to fix mistakes after publishing). Everything gated on editing uses `canEdit`.
  const canEdit = isDraft || (isAdmin && exam.status === 'Published');
  const unsectioned = exam.questions.filter(q => !q.sectionId);

  const editContent = (
    <>
      {canEdit && !isDraft && (
        <div className="card" style={{ background: 'var(--warn-soft-bg)', color: 'var(--warn-soft-fg)' }}>
          <strong>You are editing a published exam.</strong> Changes take effect immediately for
          students. Editing questions or marks after students have submitted can affect their saved
          answers and grading.
        </div>
      )}
      {editingDetails && canEdit ? (
        <ExamDetailsEditForm
          exam={exam}
          orgId={orgId}
          onCancel={() => setEditingDetails(false)}
          onSaved={async () => { setEditingDetails(false); await reload(); }}
        />
      ) : (
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18 }}>{exam.title}</h2>
              {(exam.subjectName || exam.subjectCode) && (
                <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                  {[exam.subjectName, exam.subjectCode].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            {canEdit && (
              <button onClick={() => setEditingDetails(true)}>Edit details</button>
            )}
          </div>
          {exam.batchCode && (
            <>
              <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>Batch</div>
              <div>
                {exam.batches && exam.batches.length > 0
                  ? exam.batches.map(b => `${b.code} — ${b.name}`).join(', ')
                  : `${exam.batchCode}${exam.batchName ? ` — ${exam.batchName}` : ''}`}
              </div>
            </>
          )}
          <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>Window</div>
          <div>{new Date(exam.scheduledStart).toLocaleString()} → {new Date(exam.scheduledEnd).toLocaleString()} · {exam.durationMinutes} min</div>
          {exam.instructions && <>
            <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>Instructions</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{exam.instructions}</div>
          </>}
          {exam.courseOutcomes && exam.courseOutcomes.length > 0 && <>
            <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>Course Outcomes</div>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {exam.courseOutcomes.map(c => (
                <li key={c.code}><strong>{c.code}</strong>{c.description ? ` — ${c.description}` : ''}</li>
              ))}
            </ul>
          </>}
        </div>
      )}

      {exam.sections.map(s => (
        <SectionCard key={s.id} exam={exam} section={s}
                     editable={canEdit} reload={reload} />
      ))}

      {unsectioned.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Unsectioned questions</h3>
          {unsectioned.map((q, i) =>
            <QuestionRow key={q.id} examId={exam.id} q={q} num={i + 1}
                         sections={exam.sections} editable={canEdit} reload={reload}
                         courseOutcomes={exam.courseOutcomes ?? []} />
          )}
        </div>
      )}

      {canEdit && <AddSectionForm examId={exam.id} reload={reload} />}
    </>
  );

  if (embedded) {
    return (
      <OrgLayout>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ margin: 0 }}>{exam.title}</h1>
          <div className="row" style={{ gap: 12 }}>
            <span className="muted" style={{ fontSize: 13 }}>{exam.status} · {exam.totalMarks} marks</span>
            <button onClick={() => nav(paperPath(exam.id))}>Download question paper</button>
            {isDraft && <button className="primary" onClick={publish}>Publish</button>}
          </div>
        </div>
        {editContent}
      </OrgLayout>
    );
  }

  return (
    <>
      <header className="topbar">
        <div className="brand">{exam.title} · Author</div>
        <div className="row" style={{ gap: 12 }}>
          <span style={{ fontSize: 13 }}>{exam.status} · {exam.totalMarks} marks</span>
          <button onClick={() => nav(paperPath(exam.id))} className="topbar-ghost">Download question paper</button>
          {isDraft && <button className="primary" onClick={publish}>Publish</button>}
          <button onClick={() => nav(examsHome)} className="topbar-ghost">Back</button>
        </div>
      </header>
      <div className="container">{editContent}</div>
    </>
  );
}

// --------------------- helpers ---------------------

function defaultDateLocal(hoursFromNow: number) {
  const d = new Date(Date.now() + hoursFromNow * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Format a stored ISO/UTC timestamp for a <input type="datetime-local"> (local wall-clock). */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Edit a draft exam's title/instructions/window/duration and the batches it targets. */
function ExamDetailsEditForm({ exam, orgId, onCancel, onSaved }: {
  exam: AuthorExam;
  orgId?: string;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const toast = useToast();
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [batchOptions, setBatchOptions] = useState<BatchDto[]>([]);
  const [departmentId, setDepartmentId] = useState('');
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(
    () => [...new Set((exam.batches ?? []).map(b => b.programId))]);
  const [selectedBatches, setSelectedBatches] = useState<string[]>(
    () => (exam.batches ?? []).map(b => b.id));
  const [form, setForm] = useState({
    title: exam.title,
    subjectName: exam.subjectName ?? '',
    subjectCode: exam.subjectCode ?? '',
    regulation: exam.regulation ?? '',
    instructions: exam.instructions ?? '',
    durationMinutes: exam.durationMinutes,
    scheduledStart: toLocalInput(exam.scheduledStart),
    scheduledEnd: toLocalInput(exam.scheduledEnd),
  });
  const [answerSheets, setAnswerSheets] = useState<AnswerSheetConfig>(
    exam.answerSheets ?? DEFAULT_ANSWER_SHEETS);
  const [courseOutcomes, setCourseOutcomes] = useState<CourseOutcome[]>(exam.courseOutcomes ?? []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listDepartments(orgId).then(setDepartments).catch(() => setDepartments([]));
    api.listPrograms(orgId).then(setPrograms).catch(() => setPrograms([]));
  }, [orgId]);

  const programsForForm = departmentId ? programs.filter(p => p.departmentId === departmentId) : programs;

  useEffect(() => {
    if (selectedPrograms.length === 0) { setBatchOptions([]); return; }
    let cancelled = false;
    Promise.all(selectedPrograms.map(pid => api.listBatches(pid).catch(() => [])))
      .then(lists => {
        if (cancelled) return;
        const byId = new Map<string, BatchDto>();
        lists.flat().forEach(b => byId.set(b.id, b));
        setBatchOptions([...byId.values()]);
      });
    return () => { cancelled = true; };
  }, [selectedPrograms]);

  async function save() {
    if (!form.title.trim()) { alert('Title is required.'); return; }
    setBusy(true);
    try {
      const payload: UpdateExamRequest = {
        title: form.title,
        instructions: form.instructions || null,
        scheduledStart: new Date(form.scheduledStart).toISOString(),
        scheduledEnd: new Date(form.scheduledEnd).toISOString(),
        durationMinutes: Number(form.durationMinutes),
        batchIds: selectedBatches,
        answerSheets,
        subjectName: form.subjectName.trim() || null,
        subjectCode: form.subjectCode.trim() || null,
        regulation: form.regulation.trim() || null,
        courseOutcomes: courseOutcomes.filter(c => c.code.trim()),
      };
      await api.updateExam(exam.id, payload);
      toast.success('Exam updated.');
      await onSaved();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to update exam.';
      alert(msg); toast.error(msg);
    } finally { setBusy(false); }
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Edit exam details</h2>
      <div className="col">
        <label>
          <div className="muted">Title</div>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
        </label>
        <div className="row" style={{ gap: 12 }}>
          <label style={{ flex: 1 }}>
            <div className="muted">Subject name</div>
            <input value={form.subjectName} placeholder="e.g. Data Structures"
                   onChange={e => setForm({ ...form, subjectName: e.target.value })} />
          </label>
          <label style={{ flex: '0 0 200px' }}>
            <div className="muted">Subject code</div>
            <input value={form.subjectCode} placeholder="e.g. CS8391"
                   onChange={e => setForm({ ...form, subjectCode: e.target.value })} />
          </label>
          <label style={{ flex: '0 0 200px' }}>
            <div className="muted">Regulation</div>
            <input value={form.regulation} placeholder="e.g. Regulation 2023"
                   onChange={e => setForm({ ...form, regulation: e.target.value })} />
          </label>
        </div>
        <label>
          <div className="muted">Instructions (optional)</div>
          <textarea rows={3} value={form.instructions}
                    onChange={e => setForm({ ...form, instructions: e.target.value })} />
        </label>
        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <label style={{ flex: 1 }}>
            <div className="muted">Department</div>
            <select value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
              <option value="">All</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </label>
          <div style={{ flex: 1 }}>
            <div className="muted">Program (select one or more)</div>
            <div style={{ marginTop: 4 }}>
              <MultiSelect
                options={programsForForm.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
                selected={selectedPrograms}
                onChange={setSelectedPrograms}
                placeholder="Select programs…"
                emptyText="No programs"
              />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div className="muted">Batch (students who'll see this exam)</div>
            <div style={{ marginTop: 4 }}>
              <MultiSelect
                options={batchOptions.map(c => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                selected={selectedBatches}
                onChange={setSelectedBatches}
                disabled={selectedPrograms.length === 0}
                placeholder={selectedPrograms.length === 0 ? 'Pick a program first' : 'Select batches…'}
                emptyText="No batches"
              />
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <label style={{ flex: 1 }}>
            <div className="muted">Window opens</div>
            <input type="datetime-local" value={form.scheduledStart}
                   onChange={e => setForm({ ...form, scheduledStart: e.target.value })} />
          </label>
          <label style={{ flex: 1 }}>
            <div className="muted">Window closes</div>
            <input type="datetime-local" value={form.scheduledEnd}
                   onChange={e => setForm({ ...form, scheduledEnd: e.target.value })} />
          </label>
          <label style={{ flex: '0 0 120px' }}>
            <div className="muted">Duration (min)</div>
            <input type="number" min={1} value={form.durationMinutes}
                   onChange={e => setForm({ ...form, durationMinutes: Number(e.target.value) })} />
          </label>
        </div>
        <AnswerSheetsFields value={answerSheets} onChange={setAnswerSheets} />
        <CourseOutcomesEditor value={courseOutcomes} onChange={setCourseOutcomes} />
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Either/or alternatives (same choiceGroupId) collapse into one display unit; everything
// else stays a standalone question. Order is preserved from the question list.
type QGroup =
  | { kind: 'single'; q: AuthorQuestion }
  | { kind: 'choice'; groupId: string; items: AuthorQuestion[] };

function groupByChoice(qs: AuthorQuestion[]): QGroup[] {
  const out: QGroup[] = [];
  const seen = new Set<string>();
  for (const q of qs) {
    if (q.choiceGroupId) {
      if (seen.has(q.choiceGroupId)) continue;
      seen.add(q.choiceGroupId);
      const items = qs.filter(x => x.choiceGroupId === q.choiceGroupId)
        .slice().sort((a, b) => a.choiceOrder - b.choiceOrder);
      out.push({ kind: 'choice', groupId: q.choiceGroupId, items });
    } else {
      out.push({ kind: 'single', q });
    }
  }
  return out;
}

function SectionCard({ exam, section, editable, reload }:
  { exam: AuthorExam; section: AuthorExam['sections'][number]; editable: boolean; reload: () => Promise<void> }) {
  const toast = useToast();
  const qs = exam.questions.filter(q => q.sectionId === section.id);
  const groups = groupByChoice(qs);
  const [adding, setAdding] = useState(false);
  const [addingEitherOr, setAddingEitherOr] = useState(false);
  const [editingSection, setEditingSection] = useState(false);
  const [secForm, setSecForm] = useState({ title: '', instructions: '', requiredAnswers: 0 });
  const [secBusy, setSecBusy] = useState(false);

  function openSectionEdit() {
    setSecForm({ title: section.title, instructions: section.instructions ?? '', requiredAnswers: section.requiredAnswers });
    setEditingSection(true);
  }

  async function saveSection() {
    if (!secForm.title.trim()) { alert('Title is required.'); return; }
    setSecBusy(true);
    try {
      await api.updateSection(exam.id, section.id, {
        title: secForm.title.trim(),
        instructions: secForm.instructions.trim() || null,
        requiredAnswers: Math.max(0, Number(secForm.requiredAnswers) || 0),
      });
      setEditingSection(false);
      await reload();
      toast.success('Section updated.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to update section.';
      alert(msg);
      toast.error(msg);
    } finally { setSecBusy(false); }
  }

  return (
    <div className="card">
      {editingSection ? (
        <div className="col" style={{ gap: 10 }}>
          <h3 style={{ margin: 0 }}>Edit section</h3>
          <label>
            <div className="muted">Title</div>
            <input value={secForm.title} autoFocus
                   onChange={e => setSecForm({ ...secForm, title: e.target.value })} />
          </label>
          <label>
            <div className="muted">Instructions (optional)</div>
            <textarea rows={2} value={secForm.instructions}
                      onChange={e => setSecForm({ ...secForm, instructions: e.target.value })} />
          </label>
          <label style={{ maxWidth: 280 }}>
            <div className="muted">Required answers (0 = answer all)</div>
            <input type="number" min={0} value={secForm.requiredAnswers}
                   onChange={e => setSecForm({ ...secForm, requiredAnswers: Number(e.target.value) })} />
          </label>
          <div className="muted" style={{ fontSize: 12 }}>
            Type: <strong>{questionTypeLabel(section.questionType)}</strong> (can't change after creation)
          </div>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => setEditingSection(false)} disabled={secBusy}>Cancel</button>
            <button className="primary" onClick={saveSection} disabled={secBusy}>
              {secBusy ? 'Saving…' : 'Save section'}
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: 0 }}>{section.title}</h3>
            {section.instructions && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{section.instructions}</div>}
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              {questionTypeLabel(section.questionType)} · Answer {section.requiredAnswers > 0 ? `${section.requiredAnswers} of ${qs.length}` : `all ${qs.length}`} · max {section.maxMarks} marks
            </div>
          </div>
          {editable && (
            <div className="row" style={{ gap: 8 }}>
              <button onClick={openSectionEdit}>Edit section</button>
              <button onClick={async () => {
                if (!confirm(`Delete section "${section.title}"?`)) return;
                try {
                  await api.deleteSection(exam.id, section.id);
                  await reload();
                  toast.success('Section deleted.');
                } catch (e: any) {
                  const msg = e?.message ?? 'Failed to delete section.';
                  alert(msg);
                  toast.error(msg);
                }
              }}>Delete section</button>
            </div>
          )}
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        {qs.length === 0 && <div className="muted">No questions yet.</div>}
        {groups.map((g, gi) => g.kind === 'single'
          ? <QuestionRow key={g.q.id} examId={exam.id} q={g.q} num={gi + 1}
                         sections={exam.sections} editable={editable} reload={reload}
                         courseOutcomes={exam.courseOutcomes ?? []} />
          : (
            <div key={g.groupId} className="choice-group"
                 style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
              <div className="muted" style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                {gi + 1}. ⇋ Either / Or — student answers only one
              </div>
              {g.items.map((q, i) => (
                <QuestionRow key={q.id} examId={exam.id} q={q} num={gi + 1} altLabel={i === 0 ? 'A' : 'B'}
                             sections={exam.sections} editable={editable} reload={reload}
                             courseOutcomes={exam.courseOutcomes ?? []} />
              ))}
            </div>
          ))}
      </div>
      {editable && (
        adding
          ? <AddQuestionForm exam={exam} section={section} reload={reload} onClose={() => setAdding(false)} />
          : addingEitherOr
            ? <AddEitherOrForm exam={exam} section={section} reload={reload} onClose={() => setAddingEitherOr(false)} />
            : <div className="row" style={{ marginTop: 12, gap: 8 }}>
                <button className="primary" onClick={() => setAdding(true)}>+ Create questions</button>
                <button onClick={() => setAddingEitherOr(true)}>+ Either/Or (A or B)</button>
              </div>
      )}
    </div>
  );
}

function questionTypeLabel(t: QuestionType) {
  return t === 'MultipleChoiceSingle' ? 'Multiple Choice (single)'
    : t === 'MultipleChoiceMulti' ? 'Multiple Choice (multi)'
    : 'Descriptive';
}

function QuestionRow({ examId, q, sections, editable, reload, altLabel, num, courseOutcomes }:
  { examId: string; q: AuthorQuestion; sections: AuthorExam['sections'];
    editable: boolean; reload: () => Promise<void>; altLabel?: string; num?: number;
    courseOutcomes: CourseOutcome[] }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <EditQuestionForm
        examId={examId}
        question={q}
        sections={sections}
        onClose={() => setEditing(false)}
        reload={reload}
        courseOutcomes={courseOutcomes}
      />
    );
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '12px 0' }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div className="muted" style={{ fontWeight: 400, fontSize: 13, marginBottom: 4 }}>
            {altLabel && <strong style={{ color: 'var(--accent)' }}>({altLabel}) </strong>}
            [{kindLabel(q.type)}, {q.marks}m]
            {q.courseOutcome ? ` · ${q.courseOutcome}` : ''}
            {q.bloomLevels?.length ? ` · ${q.bloomLevels.map(l => `L${l}`).join(',')}` : ''}
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            {num !== undefined && (
              <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {num}{altLabel ? ` (${altLabel})` : ''}.
              </span>
            )}
            <div style={{ flex: 1 }}>
              {isRichTextEmpty(q.prompt)
                ? <span className="muted">(no prompt)</span>
                : <RichTextView html={q.prompt} />}
            </div>
          </div>
          {open && (
            <div style={{ marginTop: 8 }}>
              {q.type === 'Descriptive' && (
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  Expected content: {(q.expectedAnswerContent?.length ? q.expectedAnswerContent : ['Text']).join(', ')}
                </div>
              )}
              {q.type === 'Descriptive' && q.parts && q.parts.length > 0 && (
                <div style={{ fontSize: 13, marginBottom: 6 }}>
                  <strong>Parts ({q.marks} marks total)</strong>
                  <ol type="a" style={{ margin: '4px 0 0', paddingLeft: 22 }}>
                    {q.parts.map(p => (
                      <li key={p.id} style={{ marginBottom: 4 }}>
                        <span className="muted">[{p.marks}m] </span>
                        {isRichTextEmpty(p.prompt) ? <span className="muted">(no prompt)</span> : <RichTextView html={p.prompt} />}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {q.type !== 'Descriptive' && (
                <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                  {q.options.map(o => (
                    <li key={o.id} style={{ color: o.isCorrect ? 'var(--success)' : undefined }}>
                      {o.isCorrect ? '✓ ' : ''}{o.text}
                    </li>
                  ))}
                </ul>
              )}
              {q.type === 'Descriptive' && !q.parts?.length && q.rubric && (
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  <strong>Rubric — {q.rubric.totalMaxPoints} pts</strong>
                  {q.rubric.aiAssistedGrading && <span className="muted"> · AI-assisted</span>}
                  {q.rubric.description && (
                    <div className="muted" style={{ marginTop: 2 }}>{q.rubric.description}</div>
                  )}
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {q.rubric.criteria.map(c => (
                      <li key={c.id}>
                        <strong>[{c.category}] {c.title}</strong> — {c.maxPoints} pts
                        {c.description && <span className="muted"> · {c.description}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {q.type === 'Descriptive' && !q.parts?.length && !q.rubric && (
                <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
                  No rubric yet. Click <strong>Edit</strong> to add criteria for graders.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button onClick={() => setOpen(o => !o)} style={{ padding: '4px 10px' }}>
            {open ? 'Hide' : 'Details'}
          </button>
          {editable && (
            <>
              <button onClick={() => setEditing(true)} style={{ padding: '4px 10px' }}>Edit</button>
              <button onClick={async () => {
                if (!confirm('Delete this question?')) return;
                await api.deleteQuestion(examId, q.id);
                await reload();
                toast.success('Question deleted.');
              }} style={{ padding: '4px 10px' }}>Delete</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AddSectionForm({ examId, reload }: { examId: string; reload: () => Promise<void> }) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [requiredAnswers, setRequiredAnswers] = useState<number>(0);
  const [questionType, setQuestionType] = useState<QuestionType>('MultipleChoiceSingle');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!title.trim()) { alert('Title required.'); return; }
    setBusy(true);
    try {
      await api.addSection(examId, { title, instructions: instructions || null, requiredAnswers, questionType });
      setTitle(''); setInstructions(''); setRequiredAnswers(0); setQuestionType('MultipleChoiceSingle');
      await reload();
      toast.success('Section added.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed.';
      alert(msg);
      toast.error(msg);
    }
    finally { setBusy(false); }
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Add section</h3>
      <div className="col">
        <label>
          <div className="muted">Title (e.g. "Section A — Concepts")</div>
          <input value={title} onChange={e => setTitle(e.target.value)} />
        </label>
        <label>
          <div className="muted">Instructions (optional)</div>
          <textarea rows={2} value={instructions} onChange={e => setInstructions(e.target.value)} />
        </label>
        <div className="row" style={{ gap: 12 }}>
          <label style={{ flex: 1 }}>
            <div className="muted">Question type (all questions in this section)</div>
            <select value={questionType} onChange={e => setQuestionType(e.target.value as QuestionType)}>
              <option value="MultipleChoiceSingle">Multiple Choice (single answer)</option>
              <option value="MultipleChoiceMulti">Multiple Choice (multi-select)</option>
              <option value="Descriptive">Descriptive (handwritten)</option>
            </select>
          </label>
          <label style={{ flex: '0 0 220px' }}>
            <div className="muted">Required answers (0 = answer all)</div>
            <input type="number" min={0} value={requiredAnswers}
                   onChange={e => setRequiredAnswers(Number(e.target.value))} />
          </label>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="primary" disabled={busy} onClick={submit}>Add section</button>
        </div>
      </div>
    </div>
  );
}

// ----- Shared question form -----

const RUBRIC_CATEGORIES: RubricCategory[] =
  ['General', 'Text', 'Diagram', 'Math', 'Chemistry', 'Table'];

// Content kinds the author can declare for a descriptive answer. Anything beyond "Text" routes
// the answer to a multimodal grader; Text-only is transcribed and graded as text.
const ANSWER_CONTENT_KINDS: AnswerContentKind[] =
  ['Text', 'Diagram', 'Math', 'Chemistry', 'Table'];

interface CriterionDraft {
  title: string;
  description: string;
  category: RubricCategory;
  maxPoints: number;
}

/** A draft sub-part in the authoring forms (descriptive only). */
interface PartDraft { prompt: string; marks: number; }

/** Sub-part label: (a), (b), (c)… */
const partLetter = (i: number) => String.fromCharCode(97 + i);

const sumPartMarks = (parts: PartDraft[]) => parts.reduce((s, p) => s + (Number(p.marks) || 0), 0);

interface QuestionFormState {
  type: QuestionType;
  sectionId: string;
  prompt: string;
  marks: number;
  options: { text: string; isCorrect: boolean }[];
  // Content kinds the answer may contain (Descriptive only) — AI-eval routing hint.
  expectedAnswerContent: AnswerContentKind[];
  // Sub-parts (Descriptive only). Non-empty means the question is split into (a)/(b)/(c)…
  // and its marks is the sum of the parts.
  parts: PartDraft[];
  // Rubric (Descriptive only). Empty `criteria` means no rubric.
  rubricDescription: string;
  aiAssistedGrading: boolean;
  criteria: CriterionDraft[];
  // Course Outcome (e.g. "CO1") + Bloom's level (1–6; 0 = none).
  courseOutcome: string;
  bloomLevels: number[];
}

const blankOptions = (): QuestionFormState['options'] =>
  [{ text: '', isCorrect: false }, { text: '', isCorrect: false }];

function emptyQuestionForm(): QuestionFormState {
  return {
    type: 'MultipleChoiceSingle',
    sectionId: '',
    prompt: '',
    marks: 1,
    options: blankOptions(),
    expectedAnswerContent: ['Text'],
    parts: [],
    rubricDescription: '',
    aiAssistedGrading: false,
    criteria: [],
    courseOutcome: '',
    bloomLevels: []
  };
}

function questionFormFromExisting(q: AuthorQuestion): QuestionFormState {
  return {
    type: q.type,
    sectionId: q.sectionId ?? '',
    prompt: q.prompt,
    marks: q.marks,
    options: q.options.length > 0
      ? q.options.map(o => ({ text: o.text, isCorrect: o.isCorrect }))
      : blankOptions(),
    expectedAnswerContent: q.expectedAnswerContent?.length ? q.expectedAnswerContent : ['Text'],
    parts: (q.parts ?? []).map(p => ({ prompt: p.prompt, marks: p.marks })),
    rubricDescription: q.rubric?.description ?? '',
    aiAssistedGrading: q.rubric?.aiAssistedGrading ?? false,
    criteria: q.rubric?.criteria.map(c => ({
      title: c.title,
      description: c.description ?? '',
      category: c.category,
      maxPoints: c.maxPoints
    })) ?? [],
    courseOutcome: q.courseOutcome ?? '',
    bloomLevels: q.bloomLevels ?? []
  };
}

function validateQuestionForm(s: QuestionFormState): string | null {
  const hasParts = s.type === 'Descriptive' && s.parts.length > 0;

  // The stem prompt is optional when the question is split into parts (the parts carry the content).
  if (!hasParts && isRichTextEmpty(s.prompt)) return 'Prompt required.';

  if (hasParts) {
    if (s.parts.some(p => isRichTextEmpty(p.prompt))) return 'Every part needs a prompt.';
    if (s.parts.some(p => Number(p.marks) <= 0)) return 'Every part\'s marks must be > 0.';
    return null; // multi-part: marks are derived, rubric is not used
  }

  if (s.marks <= 0) return 'Marks must be > 0.';

  if (s.type !== 'Descriptive') {
    const cleaned = s.options.filter(o => o.text.trim());
    if (cleaned.length < 2) return 'At least 2 options required.';
    if (!cleaned.some(o => o.isCorrect)) return 'Mark at least one option correct.';
    if (s.type === 'MultipleChoiceSingle' && cleaned.filter(o => o.isCorrect).length !== 1)
      return 'Single-answer MCQ needs exactly one correct option.';
    return null;
  }

  // Descriptive: rubric is optional, but if present it must be coherent.
  if (s.criteria.length === 0) return null;
  if (s.criteria.some(c => !c.title.trim())) return 'Every rubric criterion needs a title.';
  if (s.criteria.some(c => c.maxPoints <= 0)) return 'Every criterion\'s max points must be > 0.';
  const total = s.criteria.reduce((sum, c) => sum + Number(c.maxPoints || 0), 0);
  if (total !== s.marks)
    return `Rubric criteria total ${total} pts but the question is worth ${s.marks}. Adjust to match.`;
  return null;
}

function buildPayload(s: QuestionFormState):
    Pick<CreateQuestionRequest,
      'prompt' | 'marks' | 'allowInk' | 'allowText' | 'sectionId' | 'options' | 'expectedAnswerContent' | 'parts'
      | 'courseOutcome' | 'bloomLevels'> {
  const isMcq = s.type !== 'Descriptive';
  const hasParts = !isMcq && s.parts.length > 0;
  return {
    courseOutcome: s.courseOutcome.trim() || null,
    bloomLevels: s.bloomLevels.length ? s.bloomLevels : null,
    prompt: s.prompt,
    // For a multi-part question the marks are the sum of its parts.
    marks: hasParts ? sumPartMarks(s.parts) : s.marks,
    allowInk: !isMcq,
    allowText: false,
    sectionId: s.sectionId || null,
    options: isMcq
      ? s.options.filter(o => o.text.trim()).map(o => ({ text: o.text, isCorrect: o.isCorrect }))
      : null,
    // Routing hint is meaningful only for descriptive answers.
    expectedAnswerContent: isMcq
      ? null
      : (s.expectedAnswerContent.length ? s.expectedAnswerContent : ['Text']),
    // Send the full desired part list (empty/null = a single-prompt question).
    parts: hasParts ? s.parts.map(p => ({ prompt: p.prompt, marks: Number(p.marks) })) : null,
  };
}

// Sync the form's rubric with the server. Called after the question itself is created/updated.
// `onRubricSaved` fires only when an upsert succeeds (not on the delete path), so callers can
// surface a single "Rubric saved." toast without double-notifying.
async function syncRubric(
  examId: string,
  questionId: string,
  s: QuestionFormState,
  hadRubric: boolean,
  onRubricSaved?: () => void
) {
  if (s.type !== 'Descriptive') return;
  // A multi-part question is graded per part — it carries no question-level rubric.
  if (s.parts.length > 0) {
    if (hadRubric) await api.deleteRubric(examId, questionId);
    return;
  }
  if (s.criteria.length === 0) {
    if (hadRubric) await api.deleteRubric(examId, questionId);
    return;
  }
  const criteria: UpsertRubricCriterionRequest[] = s.criteria.map(c => ({
    title: c.title.trim(),
    description: c.description.trim() || null,
    category: c.category,
    maxPoints: Number(c.maxPoints)
  }));
  await api.upsertRubric(examId, questionId, {
    description: s.rubricDescription.trim() || null,
    aiAssistedGrading: s.aiAssistedGrading,
    criteria
  });
  onRubricSaved?.();
}

/**
 * Editor for a descriptive question's sub-parts (a)/(b)/(c)…. Each part has its own prompt and
 * marks; the student answers and is marked on each separately. Empty = a single-answer question.
 */
function PartsFields({ parts, onChange }: { parts: PartDraft[]; onChange: (next: PartDraft[]) => void }) {
  const total = sumPartMarks(parts);
  const patch = (i: number, p: Partial<PartDraft>) => onChange(parts.map((x, j) => j === i ? { ...x, ...p } : x));
  const add = () => onChange([...parts, { prompt: '', marks: 1 }]);
  const remove = (i: number) => onChange(parts.filter((_, j) => j !== i));

  return (
    <div style={{ marginTop: 4, padding: 12, border: '1px solid var(--border)', borderRadius: 6 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 14 }}>Sub-parts (a, b, c…)</strong>
        {parts.length > 0 && <span className="muted" style={{ fontSize: 12 }}>Total: {total} marks</span>}
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Split this question into parts — each is answered and marked on its own, and the question's
        marks is the sum of its parts. Leave empty for a single-answer question.
      </div>

      {parts.map((p, i) => (
        <div key={i} style={{ borderTop: i === 0 ? '1px solid var(--border)' : '1px solid var(--border)',
             paddingTop: 10, marginTop: 10 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <strong style={{ color: 'var(--accent)' }}>({partLetter(i)})</strong>
            <input type="number" min={1} value={p.marks}
                   onChange={e => patch(i, { marks: Number(e.target.value) })}
                   style={{ flex: '0 0 90px' }} title="Marks for this part" />
            <span className="muted" style={{ fontSize: 12 }}>marks</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => remove(i)}>Remove part</button>
          </div>
          <RichTextEditor value={p.prompt} onChange={html => patch(i, { prompt: html })} />
        </div>
      ))}

      <button onClick={add} style={{ marginTop: 10 }}>
        {parts.length === 0 ? '+ Split into parts' : '+ Add part'}
      </button>
    </div>
  );
}

function QuestionFields({ state, setState, sections, lockType, hideSection, courseOutcomes }: {
  state: QuestionFormState;
  setState: React.Dispatch<React.SetStateAction<QuestionFormState>>;
  sections: AuthorExam['sections'];
  lockType: boolean;
  hideSection?: boolean;
  courseOutcomes: CourseOutcome[];
}) {
  // Unique radio-group name so multiple forms (add + several edits) on screen don't
  // interfere with each other's selection state.
  const radioGroup = useId();
  const isMcq = state.type !== 'Descriptive';
  const hasParts = !isMcq && state.parts.length > 0;

  function patch(p: Partial<QuestionFormState>) {
    setState(s => ({ ...s, ...p }));
  }
  function setOpt(i: number, p: Partial<{ text: string; isCorrect: boolean }>) {
    setState(s => ({ ...s, options: s.options.map((o, j) => j === i ? { ...o, ...p } : o) }));
  }
  function addOpt() {
    setState(s => ({ ...s, options: [...s.options, { text: '', isCorrect: false }] }));
  }
  function delOpt(i: number) {
    setState(s => ({ ...s, options: s.options.filter((_, j) => j !== i) }));
  }
  function toggleContentKind(k: AnswerContentKind) {
    setState(s => {
      const has = s.expectedAnswerContent.includes(k);
      return {
        ...s,
        expectedAnswerContent: has
          ? s.expectedAnswerContent.filter(x => x !== k)
          : [...s.expectedAnswerContent, k],
      };
    });
  }

  return (
    <>
      <div className="row" style={{ gap: 12 }}>
        <label style={{ flex: 1 }}>
          <div className="muted">Type{lockType ? ' (locked)' : ''}</div>
          <select value={state.type}
                  disabled={lockType}
                  title={lockType ? 'Type cannot change after creation; delete and re-add to switch type.' : undefined}
                  onChange={e => patch({ type: e.target.value as QuestionType })}>
            <option value="MultipleChoiceSingle">Multiple Choice (single answer)</option>
            <option value="MultipleChoiceMulti">Multiple Choice (multi-select)</option>
            <option value="Descriptive">Descriptive (handwritten)</option>
          </select>
        </label>
        {!hideSection && (
          <label style={{ flex: 1 }}>
            <div className="muted">Section</div>
            <select value={state.sectionId}
                    onChange={e => patch({ sectionId: e.target.value })}>
              <option value="">— No section —</option>
              {sections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </label>
        )}
        {hasParts ? (
          <label style={{ flex: '0 0 120px' }}>
            <div className="muted">Total marks</div>
            <input type="number" value={sumPartMarks(state.parts)} disabled title="Sum of the parts' marks" />
          </label>
        ) : (
          <label style={{ flex: '0 0 100px' }}>
            <div className="muted">Marks</div>
            <input type="number" min={1} value={state.marks}
                   onChange={e => patch({ marks: Number(e.target.value) })} />
          </label>
        )}
      </div>
      <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
        <label style={{ flex: '0 0 200px' }}>
          <div className="muted">Course Outcome (CO)</div>
          <CoField value={state.courseOutcome} onChange={v => patch({ courseOutcome: v })} courseOutcomes={courseOutcomes} />
        </label>
        <div style={{ flex: 1 }}>
          <div className="muted">Bloom's level (BL)</div>
          <div style={{ marginTop: 6 }}>
            <BloomChecks value={state.bloomLevels} onChange={v => patch({ bloomLevels: v })} />
          </div>
        </div>
      </div>
      <div>
        <div className="muted" style={{ marginBottom: 4 }}>
          Question prompt{hasParts && <span className="muted"> (optional — the parts carry the question)</span>}
        </div>
        <RichTextEditor value={state.prompt} onChange={html => patch({ prompt: html })} />
      </div>

      {!isMcq && (
        <div>
          <div className="muted" style={{ marginBottom: 6 }}>
            Expected answer content{' '}
            <span
              title="Routes AI evaluation. Text-only is transcribed and graded as text (cheaper). Diagram / Math / Chemistry / Table send the page to a multimodal model."
              style={{ cursor: 'help' }}
            >ⓘ</span>
          </div>
          <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
            {ANSWER_CONTENT_KINDS.map(k => (
              <label key={k} className="row" style={{ gap: 6, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={state.expectedAnswerContent.includes(k)}
                  onChange={() => toggleContentKind(k)}
                  style={{ width: 'auto' }}
                />
                <span style={{ fontSize: 13 }}>{k}</span>
              </label>
            ))}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {state.expectedAnswerContent.some(k => k !== 'Text')
              ? 'Graded with a multimodal model (reads handwriting + visuals).'
              : 'Transcribed to text, then graded as text.'}
          </div>
        </div>
      )}

      {isMcq && (
        <div>
          <div className="muted" style={{ marginBottom: 6 }}>Options (mark the correct ones)</div>
          {state.options.map((o, i) => (
            <div key={i} className="row" style={{ gap: 8, marginBottom: 6 }}>
              <input
                type={state.type === 'MultipleChoiceSingle' ? 'radio' : 'checkbox'}
                name={radioGroup}
                checked={o.isCorrect}
                onChange={() => {
                  if (state.type === 'MultipleChoiceSingle') {
                    setState(s => ({ ...s, options: s.options.map((x, j) => ({ ...x, isCorrect: j === i })) }));
                  } else {
                    setOpt(i, { isCorrect: !o.isCorrect });
                  }
                }}
                style={{ width: 'auto' }}
              />
              <input value={o.text} placeholder={`Option ${i + 1}`}
                     onChange={e => setOpt(i, { text: e.target.value })} />
              <button onClick={() => delOpt(i)} disabled={state.options.length <= 2}>Remove</button>
            </div>
          ))}
          <button onClick={addOpt}>+ Add option</button>
        </div>
      )}

      {!isMcq && (
        <PartsFields parts={state.parts} onChange={parts => patch({ parts })} />
      )}

      {/* A rubric is for a single descriptive answer; multi-part questions are graded per part. */}
      {!isMcq && !hasParts && <RubricFields state={state} setState={setState} marks={state.marks} />}
    </>
  );
}

// The rubric-bearing fields a RubricFields host must expose. Both the standalone question form
// (QuestionFormState) and an either/or alternative (AltState) satisfy this.
interface RubricHost {
  rubricDescription: string;
  aiAssistedGrading: boolean;
  criteria: CriterionDraft[];
}

function RubricFields<T extends RubricHost>({ state, setState, marks }: {
  state: T;
  setState: React.Dispatch<React.SetStateAction<T>>;
  marks: number;
}) {
  const total = state.criteria.reduce((s, c) => s + (Number(c.maxPoints) || 0), 0);
  const matches = state.criteria.length === 0 || total === marks;

  function patchCriterion(i: number, p: Partial<CriterionDraft>) {
    setState(s => ({ ...s, criteria: s.criteria.map((c, j) => j === i ? { ...c, ...p } : c) }) as T);
  }
  function addCriterion() {
    setState(s => ({
      ...s,
      criteria: [...s.criteria, { title: '', description: '', category: 'General', maxPoints: 1 }]
    }) as T);
  }
  function removeCriterion(i: number) {
    setState(s => ({ ...s, criteria: s.criteria.filter((_, j) => j !== i) }) as T);
  }

  return (
    <div style={{ marginTop: 4, padding: 12, border: '1px solid var(--border)', borderRadius: 6 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 14 }}>Rubric (visible to graders only)</strong>
        {state.criteria.length > 0 && (
          <span style={{ fontSize: 12, color: matches ? undefined : 'var(--danger, #c00)' }}>
            Sum: {total} / {marks} pts
          </span>
        )}
      </div>

      {state.criteria.length === 0 && (
        <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
          Add criteria so graders (or AI) can score consistently. The criteria's max points
          must add up to the question's marks.
        </div>
      )}

      {state.criteria.length > 0 && (
        <>
          <label style={{ display: 'block', marginTop: 8 }}>
            <div className="muted" style={{ fontSize: 12 }}>Rubric description (optional)</div>
            <textarea rows={2} value={state.rubricDescription}
                      onChange={e => setState(s => ({ ...s, rubricDescription: e.target.value }) as T)}
                      placeholder="e.g. Award marks per criterion below; partial credit allowed." />
          </label>

          <label className="row" style={{ alignItems: 'center', gap: 6, marginTop: 8 }}>
            <input type="checkbox" checked={state.aiAssistedGrading}
                   onChange={e => setState(s => ({ ...s, aiAssistedGrading: e.target.checked }) as T)}
                   style={{ width: 'auto' }} />
            <span style={{ fontSize: 13 }}>Enable AI-assisted first-pass grading for this question</span>
          </label>

          <div style={{ marginTop: 12 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Criteria</div>
            {state.criteria.map((c, i) => (
              <div key={i} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border)', padding: '8px 0' }}>
                <div className="row" style={{ gap: 8 }}>
                  <input style={{ flex: 2 }} placeholder={`Criterion ${i + 1} title`}
                         value={c.title} onChange={e => patchCriterion(i, { title: e.target.value })} />
                  <select value={c.category}
                          onChange={e => patchCriterion(i, { category: e.target.value as RubricCategory })}
                          style={{ flex: '0 0 130px' }}>
                    {RUBRIC_CATEGORIES.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <input type="number" min={1} value={c.maxPoints}
                         onChange={e => patchCriterion(i, { maxPoints: Number(e.target.value) })}
                         style={{ flex: '0 0 80px' }} title="Max points" />
                  <button onClick={() => removeCriterion(i)}>Remove</button>
                </div>
                <textarea rows={2} placeholder="Descriptor — what earns full marks here?"
                          value={c.description}
                          onChange={e => patchCriterion(i, { description: e.target.value })}
                          style={{ marginTop: 6 }} />
              </div>
            ))}
          </div>
        </>
      )}

      <button onClick={addCriterion} style={{ marginTop: 8 }}>
        {state.criteria.length === 0 ? '+ Add rubric criterion' : '+ Add criterion'}
      </button>
    </div>
  );
}

function AddQuestionForm({ exam, section, reload, onClose }:
  { exam: AuthorExam; section: AuthorExam['sections'][number]; reload: () => Promise<void>; onClose: () => void }) {
  const toast = useToast();
  // The section dictates the type; the form opens pre-set to it and locked.
  const [state, setState] = useState<QuestionFormState>(
    () => ({ ...emptyQuestionForm(), type: section.questionType, sectionId: section.id }));
  const [busy, setBusy] = useState(false);

  async function submit() {
    const err = validateQuestionForm(state);
    if (err) { alert(err); return; }

    const payload: CreateQuestionRequest = { type: state.type, ...buildPayload(state) };
    setBusy(true);
    try {
      const newId = await api.addQuestion(exam.id, payload);
      toast.success('Question added.');
      // Rubric is a separate API call — for new questions there's no existing rubric to delete.
      await syncRubric(exam.id, newId, state, false, () => toast.success('Rubric saved.'));
      // Reset prompt/options/rubric for the next question; keep this section's type + marks.
      setState(s => ({
        ...emptyQuestionForm(),
        type: section.questionType,
        sectionId: section.id,
        marks: s.marks
      }));
      await reload();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed.';
      alert(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Add a <strong>{questionTypeLabel(section.questionType)}</strong> question to “{section.title}”.
      </div>
      <div className="col">
        <QuestionFields state={state} setState={setState}
                        sections={exam.sections} lockType={true} hideSection={true}
                        courseOutcomes={exam.courseOutcomes ?? []} />
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={busy}>Done</button>
          <button className="primary" disabled={busy} onClick={submit}>Add question</button>
        </div>
      </div>
    </div>
  );
}

// ---- Either/Or authoring ---------------------------------------------------------------

interface AltState {
  prompt: string;
  options: { text: string; isCorrect: boolean }[];
  expectedAnswerContent: AnswerContentKind[];
  // Sub-parts (Descriptive only). Non-empty means this alternative is split into (a)/(b)/(c)…
  parts: PartDraft[];
  // Rubric (Descriptive only). Empty `criteria` means no rubric.
  rubricDescription: string;
  aiAssistedGrading: boolean;
  criteria: CriterionDraft[];
  courseOutcome: string;
  bloomLevels: number[];
}

const emptyAlt = (): AltState => ({
  prompt: '', options: blankOptions(), expectedAnswerContent: ['Text'], parts: [],
  rubricDescription: '', aiAssistedGrading: false, criteria: [],
  courseOutcome: '', bloomLevels: [],
});

/** An alternative's effective total marks: the sum of its parts, else the shared per-alt marks. */
const altMarks = (a: AltState, sharedMarks: number) =>
  a.parts.length > 0 ? sumPartMarks(a.parts) : sharedMarks;

function AddEitherOrForm({ exam, section, reload, onClose }:
  { exam: AuthorExam; section: AuthorExam['sections'][number]; reload: () => Promise<void>; onClose: () => void }) {
  const toast = useToast();
  const type = section.questionType;
  const isMcq = type !== 'Descriptive';
  const [marks, setMarks] = useState(1);
  const [altA, setAltA] = useState<AltState>(emptyAlt);
  const [altB, setAltB] = useState<AltState>(emptyAlt);
  const [busy, setBusy] = useState(false);

  function validateAlt(a: AltState, label: string): string | null {
    const hasParts = !isMcq && a.parts.length > 0;
    // The alternative's stem prompt is optional when it's split into parts.
    if (!hasParts && isRichTextEmpty(a.prompt)) return `Alternative ${label}: prompt required.`;
    if (isMcq) {
      const cleaned = a.options.filter(o => o.text.trim());
      if (cleaned.length < 2) return `Alternative ${label}: at least 2 options required.`;
      if (!cleaned.some(o => o.isCorrect)) return `Alternative ${label}: mark at least one option correct.`;
      if (type === 'MultipleChoiceSingle' && cleaned.filter(o => o.isCorrect).length !== 1)
        return `Alternative ${label}: single-answer MCQ needs exactly one correct option.`;
    } else if (hasParts) {
      if (a.parts.some(p => isRichTextEmpty(p.prompt))) return `Alternative ${label}: every part needs a prompt.`;
      if (a.parts.some(p => Number(p.marks) <= 0)) return `Alternative ${label}: each part's marks must be > 0.`;
    } else if (a.criteria.length > 0) {
      // Rubric is optional, but if present it must be coherent.
      if (a.criteria.some(c => !c.title.trim())) return `Alternative ${label}: every rubric criterion needs a title.`;
      if (a.criteria.some(c => c.maxPoints <= 0)) return `Alternative ${label}: each criterion's max points must be > 0.`;
      const total = a.criteria.reduce((s, c) => s + Number(c.maxPoints || 0), 0);
      if (total !== marks) return `Alternative ${label}: rubric criteria total ${total} but the question is worth ${marks}.`;
    }
    return null;
  }

  async function saveAltRubric(questionId: string, a: AltState) {
    if (isMcq || a.parts.length > 0 || a.criteria.length === 0) return;
    await api.upsertRubric(exam.id, questionId, {
      description: a.rubricDescription.trim() || null,
      aiAssistedGrading: a.aiAssistedGrading,
      criteria: a.criteria.map(c => ({
        title: c.title.trim(),
        description: c.description.trim() || null,
        category: c.category,
        maxPoints: Number(c.maxPoints),
      })),
    });
  }

  function altPayload(a: AltState): EitherOrAlternativeRequest {
    return {
      prompt: a.prompt,
      options: isMcq ? a.options.filter(o => o.text.trim()).map(o => ({ text: o.text, isCorrect: o.isCorrect })) : null,
      expectedAnswerContent: isMcq ? null : (a.expectedAnswerContent.length ? a.expectedAnswerContent : ['Text']),
      parts: (!isMcq && a.parts.length > 0) ? a.parts.map(p => ({ prompt: p.prompt, marks: Number(p.marks) })) : null,
      courseOutcome: a.courseOutcome.trim() || null,
      bloomLevels: a.bloomLevels.length ? a.bloomLevels : null,
    };
  }

  async function submit() {
    if (marks <= 0) { alert('Marks must be > 0.'); return; }
    const err = validateAlt(altA, 'A') ?? validateAlt(altB, 'B');
    if (err) { alert(err); return; }

    // Both alternatives must be worth the same total (parts-sum or the shared marks).
    const ma = altMarks(altA, marks);
    const mb = altMarks(altB, marks);
    if (ma !== mb) {
      alert(`Both alternatives must total the same marks (A = ${ma}, B = ${mb}). Adjust the parts or marks.`);
      return;
    }

    const payload: CreateEitherOrRequest = {
      type, marks, allowInk: !isMcq, allowText: false, sectionId: section.id,
      altA: altPayload(altA), altB: altPayload(altB),
    };
    setBusy(true);
    try {
      const [altAId, altBId] = await api.addEitherOrQuestion(exam.id, payload);
      // Rubrics are a separate call per alternative (only meaningful for descriptive).
      if (altAId) await saveAltRubric(altAId, altA);
      if (altBId) await saveAltRubric(altBId, altB);
      toast.success('Either/Or question added.');
      setAltA(emptyAlt()); setAltB(emptyAlt());
      await reload();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed.';
      alert(msg); toast.error(msg);
    } finally { setBusy(false); }
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Either/Or — add two <strong>{questionTypeLabel(type)}</strong> alternatives; the student
        answers <strong>only one</strong>. Both are worth the same marks.
      </div>
      <label style={{ display: 'block', marginBottom: 8 }}>
        <div className="muted">Marks (each alternative)</div>
        <input type="number" min={1} value={marks}
               onChange={e => setMarks(Number(e.target.value))} style={{ maxWidth: 120 }} />
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Used when an alternative isn't split into parts. If you split one into parts, its total is
          the sum of those parts — and both alternatives must total the same.
        </div>
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <AlternativeFields label="A" type={type} state={altA} setState={setAltA} marks={marks}
                             courseOutcomes={exam.courseOutcomes ?? []} />
        </div>
        <div>
          <AlternativeFields label="B" type={type} state={altB} setState={setAltB} marks={marks}
                             courseOutcomes={exam.courseOutcomes ?? []} />
        </div>
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
        <button onClick={onClose} disabled={busy}>Done</button>
        <button className="primary" disabled={busy} onClick={submit}>Add either/or question</button>
      </div>
    </div>
  );
}

function AlternativeFields({ label, type, state, setState, marks, courseOutcomes }: {
  label: string;
  type: QuestionType;
  state: AltState;
  setState: React.Dispatch<React.SetStateAction<AltState>>;
  marks: number;
  courseOutcomes: CourseOutcome[];
}) {
  const radioGroup = useId();
  const isMcq = type !== 'Descriptive';
  const hasParts = !isMcq && state.parts.length > 0;

  function patch(p: Partial<AltState>) { setState(s => ({ ...s, ...p })); }
  function setOpt(i: number, p: Partial<{ text: string; isCorrect: boolean }>) {
    setState(s => ({ ...s, options: s.options.map((o, j) => j === i ? { ...o, ...p } : o) }));
  }
  function addOpt() { setState(s => ({ ...s, options: [...s.options, { text: '', isCorrect: false }] })); }
  function delOpt(i: number) { setState(s => ({ ...s, options: s.options.filter((_, j) => j !== i) })); }
  function toggleContentKind(k: AnswerContentKind) {
    setState(s => {
      const has = s.expectedAnswerContent.includes(k);
      return { ...s, expectedAnswerContent: has ? s.expectedAnswerContent.filter(x => x !== k) : [...s.expectedAnswerContent, k] };
    });
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12 }}>
      <div style={{ fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>Alternative {label}</div>
      <div className="row" style={{ gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
        <label style={{ flex: '0 0 160px' }}>
          <div className="muted">CO</div>
          <CoField value={state.courseOutcome} onChange={v => patch({ courseOutcome: v })} courseOutcomes={courseOutcomes} />
        </label>
        <div style={{ flex: 1 }}>
          <div className="muted">Bloom's level</div>
          <div style={{ marginTop: 6 }}>
            <BloomChecks value={state.bloomLevels} onChange={v => patch({ bloomLevels: v })} />
          </div>
        </div>
      </div>
      <div className="muted" style={{ marginBottom: 4 }}>Prompt</div>
      <RichTextEditor value={state.prompt} onChange={html => patch({ prompt: html })} />

      {isMcq && (
        <div style={{ marginTop: 8 }}>
          <div className="muted" style={{ marginBottom: 6 }}>Options (mark the correct ones)</div>
          {state.options.map((o, i) => (
            <div key={i} className="row" style={{ gap: 8, marginBottom: 6 }}>
              <input
                type={type === 'MultipleChoiceSingle' ? 'radio' : 'checkbox'}
                name={radioGroup}
                checked={o.isCorrect}
                onChange={() => {
                  if (type === 'MultipleChoiceSingle') {
                    setState(s => ({ ...s, options: s.options.map((x, j) => ({ ...x, isCorrect: j === i })) }));
                  } else {
                    setOpt(i, { isCorrect: !o.isCorrect });
                  }
                }}
                style={{ width: 'auto' }}
              />
              <input value={o.text} placeholder={`Option ${i + 1}`}
                     onChange={e => setOpt(i, { text: e.target.value })} />
              <button onClick={() => delOpt(i)} disabled={state.options.length <= 2}>Remove</button>
            </div>
          ))}
          <button onClick={addOpt}>+ Add option</button>
        </div>
      )}

      {!isMcq && (
        <div style={{ marginTop: 8 }}>
          <div className="muted" style={{ marginBottom: 6 }}>Expected answer content</div>
          <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
            {ANSWER_CONTENT_KINDS.map(k => (
              <label key={k} className="row" style={{ gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={state.expectedAnswerContent.includes(k)}
                       onChange={() => toggleContentKind(k)} style={{ width: 'auto' }} />
                <span style={{ fontSize: 13 }}>{k}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {!isMcq && (
        <div style={{ marginTop: 8 }}>
          <PartsFields parts={state.parts} onChange={parts => patch({ parts })} />
        </div>
      )}

      {!isMcq && !hasParts && (
        <div style={{ marginTop: 8 }}>
          <RubricFields state={state} setState={setState} marks={marks} />
        </div>
      )}
    </div>
  );
}

function EditQuestionForm({ examId, question, sections, onClose, reload, courseOutcomes }: {
  examId: string;
  question: AuthorQuestion;
  sections: AuthorExam['sections'];
  onClose: () => void;
  reload: () => Promise<void>;
  courseOutcomes: CourseOutcome[];
}) {
  const toast = useToast();
  const [state, setState] = useState<QuestionFormState>(() => questionFormFromExisting(question));
  const [busy, setBusy] = useState(false);

  async function submit() {
    const err = validateQuestionForm(state);
    if (err) { alert(err); return; }

    const payload: UpdateQuestionRequest = buildPayload(state);
    setBusy(true);
    try {
      await api.updateQuestion(examId, question.id, payload);
      toast.success('Question updated.');
      await syncRubric(examId, question.id, state, question.rubric !== null, () => toast.success('Rubric saved.'));
      await reload();
      onClose();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed.';
      alert(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '12px 0' }}>
      <div className="col">
        <div className="muted" style={{ fontSize: 12 }}>
          Editing question · [{kindLabel(question.type)}]
        </div>
        <QuestionFields state={state} setState={setState}
                        sections={sections} lockType={true}
                        courseOutcomes={courseOutcomes} />
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function kindLabel(t: QuestionType) {
  return t === 'MultipleChoiceSingle' ? 'MCQ-1'
    : t === 'MultipleChoiceMulti' ? 'MCQ-N'
    : 'Long';
}

